import React, { Suspense, useEffect } from 'react';
import SpinnerComponent from '@teetsh/app/src/components/Spinner/ScreenSpinner';
import {
  useProg,
  useProgrammationName,
  useHideTitle,
  progAtom,
  programmationIdAtom,
  programmationIsV2Atom,
} from '@teetsh/app/src/containers/Programmations/atoms/progAtoms';
import { useTranslation } from 'react-i18next';
import { UserLocale } from '@teetsh/app/src/types/user';
import { shortenUUID } from '@teetsh/app/src/utils/shortenUUID';
import { MIN_NB_OF_WEEKS_TO_GROUP_WEEKS } from '../constants';
import { formatPeriodeDates } from '../Main/formatPeriodeDates';
import { MatiereSingle } from './MatiereSingle';
import { MatieresUnified } from './MatieresUnified';
import { ProgrammationWrapper } from './ProgrammationWrapper';
import { PrintView, RowTitleOrientation } from './types';
import { LabelledDateRange } from '@teetsh/app/src/types/zone';
import { Provider } from 'jotai';
import { Programmation } from '@teetsh/app/src/types/programmations';
import { useHydrateAtoms } from 'jotai/react/utils';

export interface Props {
  hiddenPeriodes: string[];
  hiddenDomaines: string[];
  view: PrintView;
  matiereIds: string[];
  schoolyearPeriodes: LabelledDateRange[];
  fontSize?: number;
  hideTitle: boolean;
  hideMatiereTitle: boolean;
  editableTitle: boolean;
  title?: string;
  groupWeeksByPeriodes?: boolean;
  rowTitleOrientation: RowTitleOrientation;
  preventCutting: boolean;
  breakBefore: boolean;
  // Direct data prop for PrintProgrammation
}

const HydrateAtoms = ({ initialValues, children }) => {
  useHydrateAtoms(initialValues);
  return children;
};

export function PrintProgrammation(props: Omit<Props, 'fontSize'>) {
  useEffect(() => {
    window.status = 'ready';
  }, []);
  const { view } = props;
  const prog = useProg();
  const originalProgName = useProgrammationName();
  const progHideTitle = useHideTitle();
  const { t } = useTranslation(['common']);
  if (!prog) {
    return <SpinnerComponent />;
  }

  const programmationName = props.title || originalProgName;
  const filteredPeriodes = prog.periodes
    .filter(p => !props.hiddenPeriodes.includes(shortenUUID(p.id)))
    .map(p => ({
      ...p,
      subtitle:
        p.startDate && p.endDate
          ? formatPeriodeDates(
              p.startDate,
              p.endDate,
              t('common:locale.long') as UserLocale,
              '\u2192',
            )
          : undefined,
    }));

  const filteredMatieres = (
    props.matiereIds.length > 0
      ? prog.matieres.filter(m => props.matiereIds.includes(shortenUUID(m.id)))
      : prog.matieres
  ).map(m => ({
    ...m,
    domaines: m.domaines.filter(
      d => !props.hiddenDomaines.includes(shortenUUID(d.id)),
    ),
  }));

  const hideTitle = progHideTitle || props.hideTitle;

  if (view === 'periode_x_matiere' || view === 'matiere_x_periode') {
    // Dans ce mode les matières sont affichées de manière unifiée
    return (
      <ProgrammationWrapper
        programmationName={programmationName}
        editableTitle={props.editableTitle}
        hideTitle={hideTitle}
      >
        <MatieresUnified
          preventCutting={props.preventCutting}
          breakBefore={props.breakBefore}
          matieres={filteredMatieres}
          periodes={filteredPeriodes}
          view={view}
          schoolyearPeriodes={props.schoolyearPeriodes}
          groupWeeksByPeriodes={
            props.groupWeeksByPeriodes &&
            prog.view === 'week' &&
            // Si moins de 11 semaines c'est probablement qu'il y a seulement 1 période
            // donc pas besoin de groupes
            (filteredPeriodes || []).length > MIN_NB_OF_WEEKS_TO_GROUP_WEEKS
          }
          rowTitleOrientation={props.rowTitleOrientation}
        />
      </ProgrammationWrapper>
    );
  }

  return (
    // Dans ce mode les matières sont affichées de manière séquentielle
    <ProgrammationWrapper
      programmationName={programmationName}
      editableTitle={props.editableTitle}
      hideTitle={hideTitle}
    >
      {filteredMatieres.map((matiere, i) => (
        <div key={matiere.id}>
          <MatiereSingle
            view={view}
            matiere={matiere}
            hideMatiereTitle={filteredMatieres.length === 1}
            periodes={filteredPeriodes}
            schoolyearPeriodes={props.schoolyearPeriodes}
            groupWeeksByPeriodes={
              props.groupWeeksByPeriodes &&
              prog.view === 'week' &&
              // Si moins de 11 semaines c'est probablement qu'il y a seulement 1 période
              // donc pas besoin de groupes
              (filteredPeriodes || []).length > MIN_NB_OF_WEEKS_TO_GROUP_WEEKS
            }
            rowTitleOrientation={props.rowTitleOrientation}
            preventCutting={props.preventCutting}
            breakBefore={props.breakBefore}
            isFirstMatiere={i === 0}
          />
        </div>
      ))}
    </ProgrammationWrapper>
  );
}

export function PrintProgrammationWithData({
  prog,
  programmationId,
  ...otherProps
}: Props & { prog: Programmation; programmationId: string }) {
  if (!prog) return <SpinnerComponent />;

  return (
    <Provider>
      <HydrateAtoms
        initialValues={[
          [progAtom, Promise.resolve(prog)],
          [programmationIdAtom, programmationId],
          [programmationIsV2Atom, true],
        ]}
      >
        <Suspense fallback={<SpinnerComponent />}>
          <PrintProgrammation {...otherProps} />
        </Suspense>
      </HydrateAtoms>
    </Provider>
  );
}
